import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Skeleton,
  Alert,
  Snackbar,
  Divider,
  Collapse,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { ResponsiveNavbar } from "../../../components/ResponsiveNavbar/ResponsiveNavbar";
import jessProfileIcon from "../../../assets/navbar/iconperfiljesss.svg";
import jessProfileIconOff from "../../../assets/navbar/iconperfiljesssOff.svg";
import jessNameLogo from "../../../assets/navbar/nomejesss.svg";
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Webhook as WebhookIcon,
  ContentCopy as ContentCopyIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useRedux } from "../../../hooks";
import config from "../../../config";
import {
  getConnectors,
  createConnector as apiCreateConnector,
  updateConnector as apiUpdateConnector,
  deleteConnector as apiDeleteConnector,
  WebhookConfig,
} from "../../../api/oauth";
import {
  fetchAgentConnectionStatus,
  AgentConnectionStatus,
} from "../../../api/agent";
import { logger } from "../../../utils/logger";
import { getLoggedinUser } from "../../../api/apiCore";
import { useGlobalTheme } from "../../../context/GlobalThemeContext";

// Types
interface OAuthConnector {
  id: string;
  name: string;
  provider: string;
  description: string;
  image_url?: string;
  auth_url: string;
  token_url: string;
  api_url: string;
  scopes: string[];
  webhook_config?: WebhookConfig;
  status: "active" | "inactive";
  created_at: string;
  updated_at: string;
}

interface ConnectorFormData {
  name: string;
  provider: string;
  description: string;
  image_url: string;
  client_id: string;
  client_secret: string;
  auth_url: string;
  token_url: string;
  api_url: string;
  scopes: string[];
  extra_params?: Record<string, any>;
  webhook_config?: WebhookConfig;
}

// Provider logo base URL (CC0 licensed from gilbarbara/logos)
const PROVIDER_LOGOS_BASE_URL =
  "https://cdn.jsdelivr.net/gh/gilbarbara/logos/logos";

// Provider logo filenames
const PROVIDER_LOGOS: Record<string, string> = {
  google_gmail: "google-gmail.svg",
  google_calendar: "google-calendar.svg",
  instagram: "instagram-icon.svg",
  messenger: "messenger.svg",
  linkedin: "linkedin-icon.svg",
  slack: "slack-icon.svg",
  hubspot: "hubspot.svg",
  microsoft_outlook: "microsoft-icon.svg",
  microsoft_teams: "microsoft-teams.svg",
  atlassian_jira: "jira.svg",
  atlassian_confluence: "confluence.svg",
};

// Popular connector templates with webhook configurations
const CONNECTOR_TEMPLATES = [
  {
    name: "Gmail Integration",
    provider: "google_gmail",
    description:
      "Connect to Gmail to read and manage your emails with real-time push notifications",
    auth_url: "https://accounts.google.com/o/oauth2/v2/auth",
    token_url: "https://oauth2.googleapis.com/token",
    api_url: "https://gmail.googleapis.com/gmail/v1",
    scopes: [
      "https://www.googleapis.com/auth/gmail.readonly",
      "https://mail.google.com/",
      "https://www.googleapis.com/auth/userinfo.email",
    ],
    webhook_config: {
      enabled: true,
      default_events: ["mail.new", "mail.updated"],
      subscribe_url: "https://gmail.googleapis.com/gmail/v1/users/me/watch",
      unsubscribe_url: "https://gmail.googleapis.com/gmail/v1/users/me/stop",
      verification_method: "none" as const,
      extra_subscribe_params: {
        topic_name: "projects/YOUR_PROJECT_ID/topics/gmail-push",
      },
    },
  },
  {
    name: "Microsoft Outlook",
    provider: "microsoft_outlook",
    description:
      "Connect to Outlook with real-time email notifications via Microsoft Graph",
    auth_url: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
    token_url: "https://login.microsoftonline.com/common/oauth2/v2.0/token",
    api_url: "https://graph.microsoft.com/v1.0",
    scopes: [
      "https://graph.microsoft.com/Mail.Read",
      "https://graph.microsoft.com/Mail.Send",
      "https://graph.microsoft.com/User.Read",
      "offline_access",
    ],
    webhook_config: {
      enabled: true,
      default_events: ["created", "updated", "deleted"],
      subscribe_url: "https://graph.microsoft.com/v1.0/subscriptions",
      unsubscribe_url: "https://graph.microsoft.com/v1.0/subscriptions/{id}",
      verification_method: "signature" as const,
      extra_subscribe_params: {
        resource: "/me/mailFolders/inbox/messages",
      },
    },
  },
  {
    name: "Instagram Business",
    provider: "instagram",
    description:
      "Connect to Instagram for direct messages and mentions with webhook notifications",
    auth_url: "https://api.instagram.com/oauth/authorize",
    token_url: "https://api.instagram.com/oauth/access_token",
    api_url: "https://graph.instagram.com",
    scopes: ["instagram_business_basic", "instagram_business_manage_messages"],
    webhook_config: {
      enabled: true,
      default_events: ["messages", "messaging_postbacks", "mentions"],
      verification_method: "challenge" as const,
      verification_config: {
        token: "YOUR_VERIFY_TOKEN",
      },
    },
  },
  {
    name: "LinkedIn",
    provider: "linkedin",
    description: "Connect to LinkedIn for profile access and messaging",
    auth_url: "https://www.linkedin.com/oauth/v2/authorization",
    token_url: "https://www.linkedin.com/oauth/v2/accessToken",
    api_url: "https://api.linkedin.com/v2",
    scopes: ["r_liteprofile", "r_emailaddress", "w_member_social"],
    webhook_config: {
      enabled: true,
      default_events: ["SHARE", "COMMENT", "REACTION"],
      subscribe_url: "https://api.linkedin.com/v2/eventSubscriptions",
      verification_method: "signature" as const,
      verification_config: {
        secret: "YOUR_WEBHOOK_SECRET",
        header_name: "X-LinkedIn-Signature",
      },
    },
  },
  {
    name: "Slack Integration",
    provider: "slack",
    description:
      "Connect to Slack to send messages and receive event notifications",
    auth_url: "https://slack.com/oauth/v2/authorize",
    token_url: "https://slack.com/api/oauth.v2.access",
    api_url: "https://slack.com/api",
    scopes: ["channels:read", "users:read", "chat:write"],
    webhook_config: {
      enabled: true,
      default_events: ["message", "app_mention", "reaction_added"],
      verification_method: "signature" as const,
      verification_config: {
        secret: "YOUR_SIGNING_SECRET",
        header_name: "X-Slack-Signature",
        algorithm: "sha256",
      },
    },
  },
  {
    name: "Microsoft 365",
    provider: "microsoft_365",
    description:
      "Connect to Microsoft 365 to access your emails, calendar, and files",
    auth_url: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
    token_url: "https://login.microsoftonline.com/common/oauth2/v2.0/token",
    api_url: "https://graph.microsoft.com/v1.0",
    scopes: [
      "https://graph.microsoft.com/mail.read",
      "https://graph.microsoft.com/calendars.read",
    ],
  },
  {
    name: "Microsoft Teams",
    provider: "microsoft_teams",
    description:
      "Connect to Microsoft Teams for chat and channel messaging with real-time notifications",
    auth_url: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
    token_url: "https://login.microsoftonline.com/common/oauth2/v2.0/token",
    api_url: "https://graph.microsoft.com/v1.0",
    scopes: [
      "Chat.ReadWrite",
      "ChatMessage.Send",
      "ChannelMessage.Send",
      "Channel.ReadBasic.All",
      "User.Read",
      "offline_access",
    ],
    webhook_config: {
      enabled: true,
      default_events: ["created"],
      subscribe_url: "https://graph.microsoft.com/v1.0/subscriptions",
      unsubscribe_url: "https://graph.microsoft.com/v1.0/subscriptions/{id}",
      verification_method: "client_state" as const,
      subscription_ttl_hours: 1,
      extra_subscribe_params: {
        resource: "/chats/getAllMessages",
      },
    },
  },
];

const ConnectorsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { useAppSelector } = useRedux();

  const isDarkTheme = useGlobalTheme();

  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    localStorage.setItem("theme", newTheme ? "dark" : "light");
    window.dispatchEvent(
      new CustomEvent("appThemeChange", { detail: { isDark: newTheme } }),
    );
  };

  const [agentConnectionStatus, setAgentConnectionStatus] =
    useState<AgentConnectionStatus | null>(null);

  // Function to check agent connection status
  const checkAgentConnectionStatus = useCallback(async () => {
    try {
      const status = await fetchAgentConnectionStatus();
      setAgentConnectionStatus(status);
    } catch (error) {
      logger.error("Error fetching agent connection status:", error);
      const loggedUser = await getLoggedinUser();
      const fallbackStatus: AgentConnectionStatus = {
        isAgentConnected: false,
        companyId: loggedUser?.companyId || "",
        timestamp: Date.now(),
      };
      setAgentConnectionStatus(fallbackStatus);
    }
  }, []);

  // Effect to check agent status on mount and periodically
  useEffect(() => {
    checkAgentConnectionStatus();
    const intervalId = setInterval(() => {
      checkAgentConnectionStatus();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [checkAgentConnectionStatus]);

  // Check user role
  const { profileDetails } = useAppSelector((state) => ({
    profileDetails: state.Profile.profileDetails,
  }));

  const isAdmin = true; // Allow all users to access connectors

  // Localized connector templates with webhook configurations
  const CONNECTOR_TEMPLATES_LOCALIZED = [
    {
      name: t("oauth.connectors.templates.gmail"),
      provider: "google_gmail",
      description: t("oauth.connectors.templates.gmail_desc"),
      auth_url: "https://accounts.google.com/o/oauth2/v2/auth",
      token_url: "https://oauth2.googleapis.com/token",
      api_url: "https://gmail.googleapis.com/gmail/v1",
      scopes: [
        "https://www.googleapis.com/auth/gmail.readonly",
        "https://mail.google.com/",
        "https://www.googleapis.com/auth/userinfo.email",
      ],
      webhook_config: {
        enabled: true,
        default_events: ["mail.new", "mail.updated"],
        subscribe_url: "https://gmail.googleapis.com/gmail/v1/users/me/watch",
        unsubscribe_url: "https://gmail.googleapis.com/gmail/v1/users/me/stop",
        verification_method: "none" as const,
        extra_subscribe_params: {
          topic_name: "projects/YOUR_PROJECT_ID/topics/gmail-push",
        },
      },
    },
    {
      name: "Google Calendar",
      provider: "google_calendar",
      description:
        t("oauth.connectors.templates.google_calendar_desc") ||
        "Connect to Google Calendar with real-time event notifications",
      auth_url: "https://accounts.google.com/o/oauth2/v2/auth",
      token_url: "https://oauth2.googleapis.com/token",
      api_url: "https://www.googleapis.com/calendar/v3",
      scopes: [
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/calendar.events",
        "https://www.googleapis.com/auth/calendar.app.created",
        "https://www.googleapis.com/auth/calendar.readonly",
      ],
      webhook_config: {
        enabled: true,
        default_events: ["calendar.event.created", "calendar.event.updated"],
        subscribe_url:
          "https://www.googleapis.com/calendar/v3/calendars/primary/events/watch",
        verification_method: "none" as const,
      },
    },
    {
      name: t("oauth.connectors.templates.instagram") || "Instagram Business",
      provider: "instagram",
      description:
        t("oauth.connectors.templates.instagram_desc") ||
        "Connect to Instagram for DMs and mentions with webhook notifications",
      auth_url: "https://api.instagram.com/oauth/authorize",
      token_url: "https://api.instagram.com/oauth/access_token",
      api_url: "https://graph.instagram.com",
      scopes: [
        "instagram_business_basic",
        "instagram_business_manage_messages",
      ],
      webhook_config: {
        enabled: true,
        default_events: ["messages", "messaging_postbacks", "mentions"],
        verification_method: "challenge" as const,
        verification_config: {
          token: "YOUR_VERIFY_TOKEN",
        },
      },
    },
    {
      name: t("oauth.connectors.templates.messenger") || "Meta Messenger",
      provider: "messenger",
      description:
        t("oauth.connectors.templates.messenger_desc") ||
        "Connect to Facebook Messenger for messaging",
      auth_url: "https://www.facebook.com/v18.0/dialog/oauth",
      token_url: "https://graph.facebook.com/v18.0/oauth/access_token",
      api_url: "https://graph.facebook.com/v18.0",
      scopes: ["pages_messaging", "pages_read_engagement"],
      webhook_config: {
        enabled: true,
        default_events: ["messages", "message_deliveries", "message_reads"],
        verification_method: "challenge" as const,
        verification_config: {
          token: "YOUR_VERIFY_TOKEN",
        },
      },
    },
    {
      name: t("oauth.connectors.templates.linkedin") || "LinkedIn",
      provider: "linkedin",
      description:
        t("oauth.connectors.templates.linkedin_desc") ||
        "Connect to LinkedIn for profile and messaging with webhooks",
      auth_url: "https://www.linkedin.com/oauth/v2/authorization",
      token_url: "https://www.linkedin.com/oauth/v2/accessToken",
      api_url: "https://api.linkedin.com/v2",
      scopes: ["r_liteprofile", "r_emailaddress", "w_member_social"],
      webhook_config: {
        enabled: true,
        default_events: ["SHARE", "COMMENT", "REACTION"],
        subscribe_url: "https://api.linkedin.com/v2/eventSubscriptions",
        verification_method: "signature" as const,
        verification_config: {
          secret: "YOUR_WEBHOOK_SECRET",
          header_name: "X-LinkedIn-Signature",
        },
      },
    },
    {
      name: t("oauth.connectors.templates.slack"),
      provider: "slack",
      description: t("oauth.connectors.templates.slack_desc"),
      auth_url: "https://slack.com/oauth/v2/authorize",
      token_url: "https://slack.com/api/oauth.v2.access",
      api_url: "https://slack.com/api",
      scopes: ["channels:read", "users:read", "chat:write"],
      webhook_config: {
        enabled: true,
        default_events: ["message", "app_mention", "reaction_added"],
        verification_method: "signature" as const,
        verification_config: {
          secret: "YOUR_SIGNING_SECRET",
          header_name: "X-Slack-Signature",
          algorithm: "sha256",
        },
      },
    },
    {
      name: "HubSpot",
      provider: "hubspot",
      description:
        t("oauth.connectors.templates.hubspot_desc") ||
        "Connect to HubSpot CRM with webhook notifications",
      auth_url: "https://app.hubspot.com/oauth/authorize",
      token_url: "https://api.hubapi.com/oauth/v1/token",
      api_url: "https://api.hubapi.com",
      scopes: ["oauth", "crm.objects.contacts.read", "crm.objects.deals.read"],
      webhook_config: {
        enabled: true,
        default_events: [
          "contact.creation",
          "contact.propertyChange",
          "deal.creation",
        ],
        subscribe_url: "https://api.hubapi.com/webhooks/v3",
        verification_method: "signature" as const,
        verification_config: {
          secret: "YOUR_HUBSPOT_SECRET",
          header_name: "X-HubSpot-Signature-v3",
          algorithm: "sha256",
        },
      },
    },
    {
      name: t("oauth.connectors.templates.microsoft"),
      provider: "microsoft_outlook",
      description: t("oauth.connectors.templates.microsoft_desc"),
      auth_url:
        "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
      token_url: "https://login.microsoftonline.com/common/oauth2/v2.0/token",
      api_url: "https://graph.microsoft.com/v1.0",
      scopes: [
        "https://graph.microsoft.com/Mail.Read",
        "https://graph.microsoft.com/Mail.Send",
        "https://graph.microsoft.com/User.Read",
        "offline_access",
      ],
      webhook_config: {
        enabled: true,
        default_events: ["created", "updated", "deleted"],
        subscribe_url: "https://graph.microsoft.com/v1.0/subscriptions",
        unsubscribe_url: "https://graph.microsoft.com/v1.0/subscriptions/{id}",
        verification_method: "signature" as const,
        extra_subscribe_params: {
          resource: "/me/mailFolders/inbox/messages",
        },
      },
    },
    {
      name: t("oauth.connectors.templates.teams") || "Microsoft Teams",
      provider: "microsoft_teams",
      description:
        t("oauth.connectors.templates.teams_desc") ||
        "Connect to Microsoft Teams for chat and channel messaging with real-time notifications",
      auth_url:
        "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
      token_url: "https://login.microsoftonline.com/common/oauth2/v2.0/token",
      api_url: "https://graph.microsoft.com/v1.0",
      scopes: [
        "Chat.ReadWrite",
        "ChatMessage.Send",
        "ChannelMessage.Send",
        "Channel.ReadBasic.All",
        "User.Read",
        "offline_access",
      ],
      webhook_config: {
        enabled: true,
        default_events: ["created"],
        subscribe_url: "https://graph.microsoft.com/v1.0/subscriptions",
        unsubscribe_url: "https://graph.microsoft.com/v1.0/subscriptions/{id}",
        verification_method: "client_state" as const,
        subscription_ttl_hours: 1,
        extra_subscribe_params: {
          resource: "/chats/getAllMessages",
        },
      },
    },
    {
      name: "Atlassian Jira",
      provider: "atlassian_jira",
      description:
        t("oauth.connectors.templates.jira_desc") ||
        "Connect to Jira with webhook notifications for issues",
      auth_url: "https://auth.atlassian.com/authorize",
      token_url: "https://auth.atlassian.com/oauth/token",
      api_url: "https://api.atlassian.com/ex/jira/{cloud_id}",
      scopes: ["read:jira-work", "read:me", "offline_access"],
      webhook_config: {
        enabled: true,
        default_events: [
          "jira:issue_created",
          "jira:issue_updated",
          "comment_created",
        ],
        verification_method: "signature" as const,
        verification_config: {
          secret: "YOUR_JIRA_SECRET",
          header_name: "X-Atlassian-Webhook-Identifier",
        },
      },
    },
    {
      name: "Atlassian Confluence",
      provider: "atlassian_confluence",
      description:
        t("oauth.connectors.templates.confluence_desc") ||
        "Connect to Confluence with page update notifications",
      auth_url: "https://auth.atlassian.com/authorize",
      token_url: "https://auth.atlassian.com/oauth/token",
      api_url: "https://api.atlassian.com/ex/confluence/{cloud_id}",
      scopes: ["read:confluence-content.summary", "read:me", "offline_access"],
      webhook_config: {
        enabled: true,
        default_events: ["page_created", "page_updated", "comment_created"],
        verification_method: "signature" as const,
        verification_config: {
          secret: "YOUR_CONFLUENCE_SECRET",
          header_name: "X-Atlassian-Webhook-Identifier",
        },
      },
    },
  ];

  // State
  const [connectors, setConnectors] = useState<OAuthConnector[]>([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editingConnector, setEditingConnector] =
    useState<OAuthConnector | null>(null);
  const [formData, setFormData] = useState<ConnectorFormData>({
    name: "",
    provider: "",
    description: "",
    image_url: "",
    client_id: "",
    client_secret: "",
    auth_url: "",
    token_url: "",
    api_url: "",
    scopes: [],
    webhook_config: undefined,
  });
  const [scopeInput, setScopeInput] = useState("");
  const [defaultEventsInput, setDefaultEventsInput] = useState("");
  const [webhookExpanded, setWebhookExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedConnector, setSelectedConnector] =
    useState<OAuthConnector | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const fetchConnectors = async () => {
    setLoading(true);
    try {
      const response = await getConnectors();
      setConnectors(response || []);
    } catch (error) {
      logger.error("Failed to fetch connectors:", error);
      setSnackbar({
        open: true,
        message: t("oauth.connectors.failed_load"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const createConnector = async (data: ConnectorFormData) => {
    try {
      const newConnector = await apiCreateConnector(data);
      setConnectors((prev) => [...prev, newConnector]);
      setSnackbar({
        open: true,
        message: t("oauth.connectors.connector_created"),
        severity: "success",
      });
      setOpenForm(false);
      resetForm();
    } catch (error) {
      logger.error("Failed to create connector:", error);
      setSnackbar({
        open: true,
        message: t("oauth.connectors.failed_create"),
        severity: "error",
      });
    }
  };

  const updateConnector = async (id: string, data: ConnectorFormData) => {
    try {
      const updatedConnector = await apiUpdateConnector(id, data);
      setConnectors((prev) =>
        prev.map((conn) => (conn.id === id ? updatedConnector : conn)),
      );
      setSnackbar({
        open: true,
        message: t("oauth.connectors.connector_updated"),
        severity: "success",
      });
      setOpenForm(false);
      setEditingConnector(null);
      resetForm();
    } catch (error) {
      logger.error("Failed to update connector:", error);
      setSnackbar({
        open: true,
        message: t("oauth.connectors.failed_update"),
        severity: "error",
      });
    }
  };

  const deleteConnector = async (id: string) => {
    try {
      await apiDeleteConnector(id);
      setConnectors((prev) => prev.filter((conn) => conn.id !== id));
      setSnackbar({
        open: true,
        message: t("oauth.connectors.connector_deleted"),
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedConnector(null);
    } catch (error) {
      logger.error("Failed to delete connector:", error);
      setSnackbar({
        open: true,
        message: t("oauth.connectors.failed_delete"),
        severity: "error",
      });
    }
  };

  const toggleConnectorStatus = async (
    id: string,
    status: "active" | "inactive",
  ) => {
    try {
      setConnectors((prev) =>
        prev.map((conn) =>
          conn.id === id
            ? { ...conn, status, updated_at: new Date().toISOString() }
            : conn,
        ),
      );
      setSnackbar({
        open: true,
        message: t("oauth.connectors.status_updated", {
          status: t(
            status === "active"
              ? "oauth.connectors.activated"
              : "oauth.connectors.deactivated",
          ),
        }),
        severity: "success",
      });
    } catch (error) {
      logger.error("Failed to toggle connector status:", error);
      setSnackbar({
        open: true,
        message: t("oauth.connectors.failed_status_update"),
        severity: "error",
      });
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchConnectors();
    }
  }, [isAdmin]);

  const resetForm = () => {
    setFormData({
      name: "",
      provider: "",
      description: "",
      image_url: "",
      client_id: "",
      client_secret: "",
      auth_url: "",
      token_url: "",
      api_url: "",
      scopes: [],
      webhook_config: undefined,
    });
    setScopeInput("");
    setDefaultEventsInput("");
    setWebhookExpanded(false);
  };

  const handleOpenForm = (
    template?: (typeof CONNECTOR_TEMPLATES_LOCALIZED)[0],
  ) => {
    if (template) {
      setFormData({
        ...template,
        client_id: "",
        client_secret: "",
        image_url: PROVIDER_LOGOS[template.provider]
          ? `${PROVIDER_LOGOS_BASE_URL}/${PROVIDER_LOGOS[template.provider]}`
          : "",
      });
      setDefaultEventsInput(
        template.webhook_config?.default_events?.join(", ") || "",
      );
    } else {
      resetForm();
    }
    setOpenForm(true);
  };

  const handleEditConnector = (connector: OAuthConnector) => {
    setEditingConnector(connector);
    setFormData({
      name: connector.name,
      provider: connector.provider,
      description: connector.description,
      image_url: connector.image_url || "",
      client_id: "", // Don't populate sensitive data
      client_secret: "", // Don't populate sensitive data
      auth_url: connector.auth_url,
      token_url: connector.token_url,
      api_url: connector.api_url,
      scopes: connector.scopes,
      webhook_config: connector.webhook_config,
    });
    setDefaultEventsInput(
      connector.webhook_config?.default_events?.join(", ") || "",
    );
    setWebhookExpanded(!!connector.webhook_config?.enabled);
    setOpenForm(true);
    setAnchorEl(null);
  };

  // Validate webhook configuration before submission
  const validateWebhookConfig = (): string | null => {
    const config = formData.webhook_config;
    if (!config || !config.enabled) return null;

    // If webhooks are enabled, check for default_events
    if (!config.default_events || config.default_events.length === 0) {
      return (
        t("oauth.connectors.form.validation.default_events_required") ||
        "Default events are required when webhooks are enabled"
      );
    }

    // Validate verification config based on method
    const method = config.verification_method;
    const verifyConfig = config.verification_config;

    // client_state uses client-supplied state and does NOT require verification_config
    if (method === "client_state" || method === "none") {
      return null;
    }

    if (method === "signature") {
      if (!verifyConfig?.secret) {
        return (
          t("oauth.connectors.form.validation.secret_required") ||
          "Verification secret is required for signature verification"
        );
      }
    } else if (method === "challenge") {
      if (!verifyConfig?.token) {
        return (
          t("oauth.connectors.form.validation.token_required") ||
          "Verification token is required for challenge verification"
        );
      }
    } else if (method === "token") {
      if (!verifyConfig?.token) {
        return (
          t("oauth.connectors.form.validation.token_required") ||
          "Verification token is required for token verification"
        );
      }
    }

    return null;
  };

  const handleSubmit = () => {
    // Validate webhook configuration
    const webhookError = validateWebhookConfig();
    if (webhookError) {
      setSnackbar({ open: true, message: webhookError, severity: "error" });
      return;
    }

    if (editingConnector) {
      updateConnector(editingConnector.id, formData);
    } else {
      createConnector(formData);
    }
  };

  const handleAddScope = () => {
    if (scopeInput.trim() && !formData.scopes.includes(scopeInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        scopes: [...prev.scopes, scopeInput.trim()],
      }));
      setScopeInput("");
    }
  };

  const handleRemoveScope = (scope: string) => {
    setFormData((prev) => ({
      ...prev,
      scopes: prev.scopes.filter((s) => s !== scope),
    }));
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    connector: OAuthConnector,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedConnector(connector);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConnector(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  // Connectors are now available to all users

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          overflowY: "auto",
          background: isDarkTheme
            ? "linear-gradient(90deg, #4c3699 0%, #261b4d 50%, #583fb3 100%)"
            : "#EEEEEF",
          p: { xs: 2, md: 3 },
          display: "flex",
          flexDirection: "column",
          paddingTop: { xs: "60px", md: "80px" }, // Altura da navbar (mobile: 60px, desktop: 80px)
        }}
      >
        {/* Responsive Navbar */}
        <ResponsiveNavbar
          isConnected={false}
          connection={{ status: "disconnected", message: "" }}
          isDarkTheme={isDarkTheme}
          onThemeToggle={toggleTheme}
          isSidebarCollapsed={false}
          profileImage={
            agentConnectionStatus?.isAgentConnected
              ? jessProfileIcon
              : jessProfileIconOff
          }
          profileName={jessNameLogo}
          agentConnectionStatus={agentConnectionStatus}
        />
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            width: "100%",
            mt: { xs: 3, md: 4 },
          }}
        >
          {/* Header with improved design */}
          <Box
            sx={{
              background: isDarkTheme ? "rgba(30, 30, 30, 0.95)" : "white",
              borderRadius: 4,
              p: 4,
              mb: 4,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box sx={{ textAlign: "center", flex: 1 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #34A853 0%, #71c8aa 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 40, color: "white" }} />
                </Box>
                <Typography
                  variant="h4"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    mb: 1.5,
                    fontSize: "1.8rem",
                  }}
                >
                  {t("connectors")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#666",
                    maxWidth: 600,
                    mx: "auto",
                    fontWeight: 300,
                    fontSize: "1rem",
                  }}
                >
                  {t("oauth.connectors.description")}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => handleOpenForm()}
                sx={{
                  background:
                    "linear-gradient(135deg, #34A853 0%, #71c8aa 100%)",
                  color: "white",
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #2d9047 0%, #5fb799 100%)",
                  },
                }}
              >
                {t("add_connector")}
              </Button>
            </Box>
          </Box>

          {/* Templates Quick Actions */}
          <Box
            sx={{
              mb: 3,
              background: isDarkTheme ? "rgba(30, 30, 30, 0.95)" : "white",
              borderRadius: 4,
              p: 3,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 600, fontSize: "1.1rem", color: "#333", mb: 2 }}
            >
              {t("oauth.connectors.quick_templates")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {CONNECTOR_TEMPLATES_LOCALIZED.map((template, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  size="small"
                  onClick={() => handleOpenForm(template)}
                  sx={{
                    textTransform: "none",
                    borderColor: "#667eea",
                    color: "#667eea",
                    "&:hover": {
                      borderColor: "#5a6fd8",
                      backgroundColor: "rgba(102, 126, 234, 0.04)",
                    },
                  }}
                >
                  {template.name}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Connectors Grid */}
          {loading ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 2,
              }}
            >
              {Array.from({ length: 6 }).map((_, index) => (
                <Card key={index}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" />
                        <Skeleton variant="text" width="50%" />
                      </Box>
                    </Box>
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="80%" />
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : connectors.length === 0 ? (
            <Box
              sx={{
                background: isDarkTheme ? "rgba(30, 30, 30, 0.95)" : "white",
                borderRadius: 4,
                p: 6,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <LinkIcon
                sx={{ fontSize: 48, opacity: 0.3, mb: 2, color: "#667eea" }}
              />
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: 600, color: "#333" }}
              >
                {t("oauth.connectors.no_connectors")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }} mb={3}>
                {t("oauth.connectors.create_first_desc")}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenForm()}
                sx={{
                  background:
                    "linear-gradient(135deg, #34A853 0%, #71c8aa 100%)",
                }}
              >
                {t("oauth.connectors.create_first")}
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 2,
              }}
            >
              {connectors.map((connector) => (
                <Card
                  key={connector.id}
                  sx={{
                    position: "relative",
                    background: isDarkTheme
                      ? "rgba(30, 30, 30, 0.95)"
                      : "white",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "300px",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      {connector.image_url ? (
                        <Box
                          component="img"
                          src={connector.image_url}
                          alt={connector.name}
                          sx={{ width: 40, height: 40, borderRadius: 1 }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            backgroundColor: theme.palette.primary.main,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "1.2rem",
                          }}
                        >
                          {connector.name.charAt(0)}
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          noWrap
                          sx={{
                            fontWeight: 600,
                            fontSize: "1.1rem",
                            color: "#333",
                          }}
                        >
                          {connector.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#666" }}
                          noWrap
                        >
                          {connector.provider}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, connector)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        color: "#666",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "40px",
                      }}
                    >
                      {connector.description}
                    </Typography>

                    <Box sx={{ mt: "auto" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Chip
                          label={t(
                            `oauth.connectors.status.${connector.status}`,
                          )}
                          color={
                            connector.status === "active"
                              ? "success"
                              : "default"
                          }
                          size="small"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={connector.status === "active"}
                              onChange={(e) =>
                                toggleConnectorStatus(
                                  connector.id,
                                  e.target.checked ? "active" : "inactive",
                                )
                              }
                              size="small"
                            />
                          }
                          label=""
                          sx={{ mr: 0 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          gap: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        {connector.scopes.slice(0, 2).map((scope, index) => (
                          <Chip
                            key={index}
                            label={scope.split("/").pop() || scope}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        ))}
                        {connector.scopes.length > 2 && (
                          <Chip
                            label={`+${connector.scopes.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() =>
                selectedConnector && handleEditConnector(selectedConnector)
              }
            >
              <EditIcon sx={{ mr: 1, fontSize: 20 }} />
              {t("edit")}
            </MenuItem>
            <MenuItem onClick={handleDeleteClick}>
              <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
              {t("delete")}
            </MenuItem>
          </Menu>

          {/* Create/Edit Form Dialog */}
          <Dialog
            open={openForm}
            onClose={() => {
              setOpenForm(false);
              setEditingConnector(null);
              resetForm();
            }}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                maxHeight: "80vh",
                height: isMobile ? "100vh" : "auto",
              },
            }}
          >
            <DialogTitle
              sx={{
                background: "linear-gradient(90deg, #93F090 0%, #60BFB7 100%)",
                color: "#fff",
                py: 2,
                px: 3,
              }}
            >
              {editingConnector
                ? t("oauth.connectors.edit_connector")
                : t("oauth.connectors.create_new")}
            </DialogTitle>
            <DialogContent
              sx={{
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#555",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  pt: 2,
                }}
              >
                <TextField
                  label={t("oauth.connectors.form.connector_name")}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("oauth.connectors.form.provider_id")}
                  value={formData.provider}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      provider: e.target.value,
                    }))
                  }
                  required
                  fullWidth
                  size="small"
                  helperText={t("oauth.connectors.form.provider_id_help")}
                />
                <TextField
                  label={t("oauth.connectors.form.description")}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  required
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("oauth.connectors.form.logo_url")}
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      image_url: e.target.value,
                    }))
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("oauth.connectors.form.client_id")}
                  value={formData.client_id}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      client_id: e.target.value,
                    }))
                  }
                  required
                  fullWidth
                  size="small"
                  type="password"
                />
                <TextField
                  label={t("oauth.connectors.form.client_secret")}
                  value={formData.client_secret}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      client_secret: e.target.value,
                    }))
                  }
                  required
                  fullWidth
                  size="small"
                  type="password"
                />
                <TextField
                  label={t("oauth.connectors.form.authorization_url")}
                  value={formData.auth_url}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      auth_url: e.target.value,
                    }))
                  }
                  required
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("oauth.connectors.form.token_url")}
                  value={formData.token_url}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      token_url: e.target.value,
                    }))
                  }
                  required
                  fullWidth
                  size="small"
                />
                <TextField
                  label={t("oauth.connectors.form.api_url")}
                  value={formData.api_url}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      api_url: e.target.value,
                    }))
                  }
                  required
                  fullWidth
                  size="small"
                />

                {/* Scopes */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("oauth.connectors.form.scopes")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      placeholder={t("oauth.connectors.form.add_scope")}
                      value={scopeInput}
                      onChange={(e) => setScopeInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleAddScope()}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddScope}
                      disabled={!scopeInput.trim()}
                    >
                      {t("common.add")}
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {formData.scopes.map((scope, index) => (
                      <Chip
                        key={index}
                        label={scope}
                        onDelete={() => handleRemoveScope(scope)}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>

                {/* Webhook Configuration */}
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      py: 1,
                    }}
                    onClick={() => setWebhookExpanded(!webhookExpanded)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <WebhookIcon sx={{ color: "#667eea" }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {t("oauth.connectors.form.webhook_config")}
                      </Typography>
                    </Box>
                    <ExpandMoreIcon
                      sx={{
                        transform: webhookExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                    />
                  </Box>
                  <Collapse in={webhookExpanded}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        pt: 1,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.webhook_config?.enabled || false}
                            onChange={(e) =>
                              setFormData((prev) => {
                                // Preserve existing webhook config when toggling
                                const existingConfig =
                                  prev.webhook_config || {};
                                return {
                                  ...prev,
                                  webhook_config: {
                                    ...existingConfig,
                                    enabled: e.target.checked,
                                  },
                                };
                              })
                            }
                          />
                        }
                        label={t("oauth.connectors.form.enable_webhooks")}
                      />

                      {formData.webhook_config?.enabled && (
                        <>
                          <FormControl fullWidth size="small">
                            <InputLabel>
                              {t("oauth.connectors.form.verification_method")}
                            </InputLabel>
                            <Select
                              value={
                                formData.webhook_config?.verification_method ||
                                "none"
                              }
                              label={t(
                                "oauth.connectors.form.verification_method",
                              )}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  webhook_config: {
                                    ...prev.webhook_config,
                                    enabled: true,
                                    verification_method: e.target.value as
                                      | "challenge"
                                      | "signature"
                                      | "token"
                                      | "none"
                                      | "client_state",
                                  },
                                }))
                              }
                            >
                              <MenuItem value="none">
                                {t("oauth.connectors.form.verification_none")}
                              </MenuItem>
                              <MenuItem value="challenge">
                                {t(
                                  "oauth.connectors.form.verification_challenge",
                                )}
                              </MenuItem>
                              <MenuItem value="signature">
                                {t(
                                  "oauth.connectors.form.verification_signature",
                                )}
                              </MenuItem>
                              <MenuItem value="token">
                                {t(
                                  "oauth.connectors.form.verification_token_method",
                                )}
                              </MenuItem>
                              <MenuItem value="client_state">
                                {t(
                                  "oauth.connectors.form.verification_client_state",
                                )}
                              </MenuItem>
                            </Select>
                          </FormControl>

                          {(formData.webhook_config?.verification_method ===
                            "token" ||
                            formData.webhook_config?.verification_method ===
                              "signature") && (
                            <TextField
                              label={t(
                                "oauth.connectors.form.verification_secret",
                              )}
                              value={
                                formData.webhook_config?.verification_config
                                  ?.secret || ""
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  webhook_config: {
                                    ...prev.webhook_config,
                                    enabled: true,
                                    verification_config: {
                                      ...prev.webhook_config
                                        ?.verification_config,
                                      secret: e.target.value,
                                    },
                                  },
                                }))
                              }
                              fullWidth
                              size="small"
                              type="password"
                              helperText={t(
                                "oauth.connectors.form.verification_secret_help",
                              )}
                            />
                          )}

                          {formData.webhook_config?.verification_method ===
                            "challenge" && (
                            <>
                              <TextField
                                label={t(
                                  "oauth.connectors.form.challenge_param",
                                )}
                                value={
                                  formData.webhook_config?.verification_config
                                    ?.challenge_param || "hub.challenge"
                                }
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    webhook_config: {
                                      ...prev.webhook_config,
                                      enabled: true,
                                      verification_config: {
                                        ...prev.webhook_config
                                          ?.verification_config,
                                        challenge_param: e.target.value,
                                      },
                                    },
                                  }))
                                }
                                fullWidth
                                size="small"
                                helperText={t(
                                  "oauth.connectors.form.challenge_param_help",
                                )}
                              />
                              <TextField
                                label={t("oauth.connectors.form.token_param")}
                                value={
                                  formData.webhook_config?.verification_config
                                    ?.token_param || "hub.verify_token"
                                }
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    webhook_config: {
                                      ...prev.webhook_config,
                                      enabled: true,
                                      verification_config: {
                                        ...prev.webhook_config
                                          ?.verification_config,
                                        token_param: e.target.value,
                                      },
                                    },
                                  }))
                                }
                                fullWidth
                                size="small"
                              />
                              <TextField
                                label={t(
                                  "oauth.connectors.form.verification_token",
                                )}
                                value={
                                  formData.webhook_config?.verification_config
                                    ?.token || ""
                                }
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    webhook_config: {
                                      ...prev.webhook_config,
                                      enabled: true,
                                      verification_config: {
                                        ...prev.webhook_config
                                          ?.verification_config,
                                        token: e.target.value,
                                      },
                                    },
                                  }))
                                }
                                fullWidth
                                size="small"
                                type="password"
                              />
                            </>
                          )}

                          <TextField
                            label={t("oauth.connectors.form.subscription_ttl")}
                            value={
                              formData.webhook_config?.subscription_ttl_hours ||
                              ""
                            }
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                webhook_config: {
                                  ...prev.webhook_config,
                                  enabled: true,
                                  subscription_ttl_hours:
                                    parseInt(e.target.value) || undefined,
                                },
                              }))
                            }
                            fullWidth
                            size="small"
                            type="number"
                            helperText={t(
                              "oauth.connectors.form.subscription_ttl_help",
                            )}
                          />

                          {/* Default Events - CRITICAL for auto-subscribe */}
                          <TextField
                            label={t("oauth.connectors.form.default_events")}
                            value={defaultEventsInput}
                            onChange={(e) => {
                              setDefaultEventsInput(e.target.value);
                              const events = e.target.value
                                .split(",")
                                .map((s) => s.trim())
                                .filter((s) => s.length > 0);
                              setFormData((prev) => ({
                                ...prev,
                                webhook_config: {
                                  ...prev.webhook_config,
                                  enabled: true,
                                  default_events: events,
                                },
                              }));
                            }}
                            fullWidth
                            size="small"
                            placeholder="created, updated, deleted"
                            helperText={t(
                              "oauth.connectors.form.default_events_help",
                            )}
                          />

                          {/* Provider-specific URLs */}
                          <Divider sx={{ my: 1 }} />
                          <Typography
                            variant="caption"
                            sx={{ color: "#666", mb: 1 }}
                          >
                            {t("oauth.connectors.form.provider_urls_section")}
                          </Typography>

                          <TextField
                            label={t("oauth.connectors.form.subscribe_url")}
                            value={formData.webhook_config?.subscribe_url || ""}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                webhook_config: {
                                  ...prev.webhook_config,
                                  enabled: true,
                                  subscribe_url: e.target.value,
                                },
                              }))
                            }
                            fullWidth
                            size="small"
                            placeholder="https://api.example.com/webhooks/subscribe"
                            helperText={t(
                              "oauth.connectors.form.subscribe_url_help",
                            )}
                          />

                          <TextField
                            label={t("oauth.connectors.form.unsubscribe_url")}
                            value={
                              formData.webhook_config?.unsubscribe_url || ""
                            }
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                webhook_config: {
                                  ...prev.webhook_config,
                                  enabled: true,
                                  unsubscribe_url: e.target.value,
                                },
                              }))
                            }
                            fullWidth
                            size="small"
                            placeholder="https://api.example.com/webhooks/{subscription_id}"
                          />

                          {/* Google Pub/Sub specific */}
                          {(formData.provider
                            ?.toLowerCase()
                            .includes("google") ||
                            formData.provider
                              ?.toLowerCase()
                              .includes("gmail")) && (
                            <TextField
                              label={t("oauth.connectors.form.pubsub_topic")}
                              value={
                                formData.webhook_config?.extra_subscribe_params
                                  ?.topic_name || ""
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  webhook_config: {
                                    ...prev.webhook_config,
                                    enabled: true,
                                    extra_subscribe_params: {
                                      ...prev.webhook_config
                                        ?.extra_subscribe_params,
                                      topic_name: e.target.value,
                                    },
                                  },
                                }))
                              }
                              fullWidth
                              size="small"
                              placeholder="projects/your-project/topics/gmail-notifications"
                              helperText={t(
                                "oauth.connectors.form.pubsub_topic_help",
                              )}
                            />
                          )}

                          {/* Microsoft Graph specific */}
                          {(formData.provider
                            ?.toLowerCase()
                            .includes("microsoft") ||
                            formData.provider
                              ?.toLowerCase()
                              .includes("outlook")) && (
                            <TextField
                              label={t("oauth.connectors.form.graph_resource")}
                              value={
                                formData.webhook_config?.extra_subscribe_params
                                  ?.resource || ""
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  webhook_config: {
                                    ...prev.webhook_config,
                                    enabled: true,
                                    extra_subscribe_params: {
                                      ...prev.webhook_config
                                        ?.extra_subscribe_params,
                                      resource: e.target.value,
                                    },
                                  },
                                }))
                              }
                              fullWidth
                              size="small"
                              placeholder="/me/mailFolders('Inbox')/messages"
                              helperText={t(
                                "oauth.connectors.form.graph_resource_help",
                              )}
                            />
                          )}

                          {/* Meta/Instagram/Facebook/Messenger webhook setup instructions */}
                          {(formData.provider
                            ?.toLowerCase()
                            .includes("instagram") ||
                            formData.provider
                              ?.toLowerCase()
                              .includes("facebook") ||
                            formData.provider?.toLowerCase().includes("meta") ||
                            formData.provider
                              ?.toLowerCase()
                              .includes("messenger")) && (
                            <Alert
                              severity="info"
                              icon={<InfoIcon />}
                              sx={{
                                mt: 2,
                                "& .MuiAlert-message": { width: "100%" },
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, mb: 1 }}
                              >
                                {t(
                                  "oauth.connectors.form.meta_webhook_setup_title",
                                ) || "Manual Webhook Configuration Required"}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {t(
                                  "oauth.connectors.form.meta_webhook_setup_desc",
                                ) ||
                                  "Meta/Instagram webhooks must be configured manually in the Meta Developer Console. Follow these steps:"}
                              </Typography>

                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mb: 0.5 }}
                                >
                                  {t(
                                    "oauth.connectors.form.webhook_callback_url",
                                  ) || "Webhook Callback URL:"}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    bgcolor: "#f5f5f5",
                                    p: 1,
                                    borderRadius: 1,
                                    fontFamily: "monospace",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      flex: 1,
                                      fontFamily: "monospace",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {`${config.CLIENT_URL}/api/oauth/webhooks/${formData.provider?.toLowerCase() || "instagram"}`}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      navigator.clipboard.writeText(
                                        `${config.CLIENT_URL}/api/oauth/webhooks/${formData.provider?.toLowerCase() || "instagram"}`,
                                      );
                                      setSnackbar({
                                        open: true,
                                        message:
                                          t("common.copied") ||
                                          "Copied to clipboard",
                                        severity: "success",
                                      });
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mb: 0.5 }}
                                >
                                  {t("oauth.connectors.form.verify_token") ||
                                    "Verify Token:"}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    bgcolor: "#f5f5f5",
                                    p: 1,
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      flex: 1,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {formData.webhook_config
                                      ?.verification_config?.token ||
                                      "YOUR_VERIFY_TOKEN"}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      navigator.clipboard.writeText(
                                        formData.webhook_config
                                          ?.verification_config?.token ||
                                          "YOUR_VERIFY_TOKEN",
                                      );
                                      setSnackbar({
                                        open: true,
                                        message:
                                          t("common.copied") ||
                                          "Copied to clipboard",
                                        severity: "success",
                                      });
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Typography variant="body2" component="div">
                                <strong>
                                  {t("oauth.connectors.form.setup_steps") ||
                                    "Setup Steps:"}
                                </strong>
                                <ol
                                  style={{
                                    margin: "8px 0",
                                    paddingLeft: "20px",
                                  }}
                                >
                                  <li>
                                    {t("oauth.connectors.form.meta_step_1") ||
                                      "Go to Meta Developer Console → Your App → Webhooks"}
                                  </li>
                                  <li>
                                    {t("oauth.connectors.form.meta_step_2") ||
                                      'Click "Add Subscription" for Instagram or Messenger'}
                                  </li>
                                  <li>
                                    {t("oauth.connectors.form.meta_step_3") ||
                                      "Enter the Callback URL and Verify Token shown above"}
                                  </li>
                                  <li>
                                    {t("oauth.connectors.form.meta_step_4") ||
                                      "Select the events you want to subscribe to (messages, mentions, etc.)"}
                                  </li>
                                  <li>
                                    {t("oauth.connectors.form.meta_step_5") ||
                                      'Click "Verify and Save"'}
                                  </li>
                                </ol>
                              </Typography>
                            </Alert>
                          )}

                          {/* LinkedIn/Slack webhook info */}
                          {(formData.provider
                            ?.toLowerCase()
                            .includes("linkedin") ||
                            formData.provider
                              ?.toLowerCase()
                              .includes("slack")) && (
                            <Alert
                              severity="info"
                              icon={<InfoIcon />}
                              sx={{ mt: 2 }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, mb: 1 }}
                              >
                                {t(
                                  "oauth.connectors.form.webhook_callback_url",
                                ) || "Webhook Callback URL"}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  bgcolor: "#f5f5f5",
                                  p: 1,
                                  borderRadius: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    flex: 1,
                                    fontFamily: "monospace",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {`${config.CLIENT_URL}/api/oauth/webhooks/${formData.provider?.toLowerCase() || "provider"}`}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      `${config.CLIENT_URL}/api/oauth/webhooks/${formData.provider?.toLowerCase() || "provider"}`,
                                    );
                                    setSnackbar({
                                      open: true,
                                      message:
                                        t("common.copied") ||
                                        "Copied to clipboard",
                                      severity: "success",
                                    });
                                  }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{ mt: 1, color: "#666" }}
                              >
                                {formData.provider
                                  ?.toLowerCase()
                                  .includes("linkedin")
                                  ? t(
                                      "oauth.connectors.form.linkedin_webhook_info",
                                    ) ||
                                    "Configure this URL in LinkedIn Developer Portal → Webhooks"
                                  : t(
                                      "oauth.connectors.form.slack_webhook_info",
                                    ) ||
                                    "Configure this URL in Slack App Settings → Event Subscriptions → Request URL"}
                              </Typography>
                            </Alert>
                          )}
                        </>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Button
                onClick={() => {
                  setOpenForm(false);
                  setEditingConnector(null);
                  resetForm();
                }}
                sx={{
                  textTransform: "none",
                  px: 3,
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={
                  !formData.name || !formData.provider || !formData.client_id
                }
                sx={{
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg, #8a4fff 0%, #6b73ff 100%)",
                  px: 3,
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #7a3fef 0%, #5b63ef 100%)",
                  },
                }}
              >
                {editingConnector ? t("common.update") : t("common.create")}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
          >
            <DialogTitle>{t("oauth.connectors.delete_connector")}</DialogTitle>
            <DialogContent>
              <Typography>
                {t("oauth.connectors.delete_confirm")}
                <b>{selectedConnector?.name}</b>?{" "}
                {t("oauth.connectors.delete_warning")}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() =>
                  selectedConnector && deleteConnector(selectedConnector.id)
                }
              >
                {t("common.delete")}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          >
            <Alert
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              severity={snackbar.severity}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </>
  );
};

export default ConnectorsPage;
